import { Role } from "common/Constants";
import type { BulkEventResetResult } from "services/eventsService";
import type {
  EligibleTripsResponse,
  EventResetBatchDetail,
  EventResetBatchTripRow,
  QueuedBulkResetResponse,
} from "services/eventResetsService";

// Bulk Event Reset from the review queue. Kept out of ReviewQueue.tsx so the gate, the cap, the
// chunking and the result wording can be tested without rendering the grid.

// Most trips one submission may reset. OrderApi enforces the same limit (BulkEventReset:BatchLimit,
// 50); checking here first tells the user before anything is sent.
export const MAX_BULK_EVENT_RESET = 50;

// Every trip is a full GPS rebuild and OrderApi runs them one after another, so a single
// 50-trip request can outlast the gateway timeout. Send the selection this many at a time.
export const BULK_EVENT_RESET_CHUNK_SIZE = 10;

// Bulk reset is Admin only (requirements FR-3); the closer's single Event Reset button still allows
// TripAdmin. OrderApi enforces it again, so this only decides whether the queue offers the action.
export const canBulkResetEvents = (isUserInRole: (role: string) => boolean): boolean =>
  isUserInRole(Role.Admin);

// Why the current selection cannot be reset, or null when it can.
export const bulkResetSelectionError = (count: number): string | null => {
  if (count <= 0) return "Select at least one trip to reset.";
  if (count > MAX_BULK_EVENT_RESET)
    return `Reset at most ${MAX_BULK_EVENT_RESET} trips at a time (${count} selected).`;
  return null;
};

export const bulkResetConfirmText = (count: number): string =>
  `Reset events on ${count} trip${count === 1 ? "" : "s"}? For each trip, every event except Begin Trip ` +
  "and End Trip is deleted - including events and waits entered by the driver - and the trip is rebuilt " +
  "from GPS. The suggested events are then accepted and each trip is run through the auto-approver, which " +
  "approves it or records why it failed. Ineligible trips are skipped untouched. This cannot be undone.";

export const chunkTripIds = (tripIds: number[], size = BULK_EVENT_RESET_CHUNK_SIZE): number[][] => {
  const chunks: number[][] = [];
  for (let i = 0; i < tripIds.length; i += size) chunks.push(tripIds.slice(i, i + size));
  return chunks;
};

// succeeded: reset and suggestions accepted. warning: the trip was (or may have been) changed but
// did not finish cleanly, so someone has to look at it. failed: the trip was not touched.
// skipped: failed an eligibility rule (requirements section 4) and was not touched.
export type BulkResetOutcome = "succeeded" | "warning" | "failed" | "skipped";

export interface BulkResetSummaryRow {
  tripId: number;
  outcome: BulkResetOutcome;
  detail: string;
}

export interface BulkResetSummary {
  succeeded: number;
  warnings: number;
  failed: number;
  skipped: number;
  rows: BulkResetSummaryRow[];
  // Set when a request failed outright and the run stopped.
  error: string | null;
  // The run's batch in the Event Resets history, when OrderApi recorded one.
  batchId: string | null;
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

// What the auto-approver decided right after the reset.
const approvalText = (r: BulkEventResetResult): string | null => {
  if (r.autoApproved) return "Auto-approved.";
  if (r.autoApproveRan) return `Auto-approve failed: ${r.autoApproveFailReason || "see the trip"}.`;
  if (r.autoApproveError) return `Auto-approve did not run: ${r.autoApproveError}`;
  return null;
};

const toRow = (r: BulkEventResetResult): BulkResetSummaryRow => {
  if (r.skipped) {
    return { tripId: r.tripId, outcome: "skipped", detail: r.message || "Not eligible for bulk reset." };
  }
  if (!r.succeeded) {
    // A reset that went through (no blocked reason) and then fell over accepting suggestions has
    // already replaced the trip's events - that is not the same as a trip left untouched.
    if (r.reset && !r.reset.blockedReason) {
      return {
        tripId: r.tripId,
        outcome: "warning",
        detail: r.message || "The events were reset, but not all suggestions were accepted. Open the trip to finish it.",
      };
    }
    return { tripId: r.tripId, outcome: "failed", detail: r.message || r.reset?.blockedReason || "The reset failed." };
  }
  const parts: string[] = [];
  if (r.reset) {
    parts.push(
      `Removed ${plural(r.reset.deactivatedEventCount, "event")}, rebuilt ${r.reset.regeneratedSuggestionCount} from GPS`
    );
  }
  parts.push(`accepted ${plural(r.acceptedSuggestionCount, "suggestion")}`);
  const text = parts.join(", ");
  const sentence = text.charAt(0).toUpperCase() + text.slice(1) + ".";
  const detail = [sentence, approvalText(r), r.message].filter(Boolean).join(" ");
  return { tripId: r.tripId, outcome: "succeeded", detail };
};

const OUTCOME_ORDER: Record<BulkResetOutcome, number> = { failed: 0, warning: 1, skipped: 2, succeeded: 3 };

const buildSummary = (rows: BulkResetSummaryRow[], error: string | null, batchId: string | null = null): BulkResetSummary => ({
  succeeded: rows.filter((r) => r.outcome === "succeeded").length,
  warnings: rows.filter((r) => r.outcome === "warning").length,
  failed: rows.filter((r) => r.outcome === "failed").length,
  skipped: rows.filter((r) => r.outcome === "skipped").length,
  // Problems first so they are not lost under a long list of successes; stable within a group.
  rows: [...rows].sort((a, b) => OUTCOME_ORDER[a.outcome] - OUTCOME_ORDER[b.outcome]),
  error,
  batchId,
});

// A trip that was sent but that OrderApi returned nothing for may or may not have been reset.
const NO_RESULT = "No result came back for this trip; it may have been reset. Open it to check its events.";

const rowsFor = (tripIds: number[], results: BulkEventResetResult[] | null | undefined): BulkResetSummaryRow[] => {
  const byTrip = new Map((results ?? []).map((r) => [r.tripId, r]));
  return tripIds.map((tripId) => {
    const r = byTrip.get(tripId);
    return r ? toRow(r) : { tripId, outcome: "warning", detail: NO_RESULT };
  });
};

// A 400, 401, 403 or 409 is a refusal before anything ran; any other failure may have happened mid-batch.
const wasRefused = (error: any) => [400, 401, 403, 409].includes(error?.response?.status);

// The reason a request was refused, from either body shape: the BFF's own { error }, or a bare
// string (how OrderApi words a 400). A bare string on a 5xx is the global handler's raw exception
// text, not a reason, so it is left for the caller's generic message.
export const responseErrorText = (error: any): string | undefined => {
  const data = error?.response?.data;
  if (typeof data === "string") {
    const status = error?.response?.status;
    return status >= 400 && status < 500 && data.trim() ? data.trim() : undefined;
  }
  return typeof data?.error === "string" && data.error ? data.error : undefined;
};

// The whole call failed (as opposed to individual trips). The BFF passes OrderApi's refusals (400,
// 401, 403, 409) through with its reason; anything else (a timeout, the global handler's raw
// exception text) is reported generically, since the server may have got part way through the list.
export const bulkResetErrorMessage = (error: any): string => {
  const status = error?.response?.status;
  if (status === 403) return "You do not have permission to reset events.";
  if (wasRefused(error))
    return responseErrorText(error) || "The request was rejected.";
  return "Bulk event reset failed. Some trips may already have been reset - check them before retrying.";
};


// Sends the selection in sequential chunks and gathers one row per trip. Stops at the first
// request that fails outright: its trips are reported by whether they could have been touched,
// and the trips never sent are reported as failed so nothing in the selection goes missing.
export const runBulkEventReset = async (
  tripIds: number[],
  resetChunk: (chunk: number[], batchId: string | null, isFinalChunk: boolean) => Promise<BulkEventResetResult[]>,
  onProgress?: (through: number, total: number) => void
): Promise<BulkResetSummary> => {
  const rows: BulkResetSummaryRow[] = [];
  const chunks = chunkTripIds(tripIds);
  let sent = 0;
  // The first chunk's results carry the batch every later chunk joins.
  let batchId: string | null = null;
  for (let i = 0; i < chunks.length; i++) {
    const chunk = chunks[i];
    onProgress?.(sent + chunk.length, tripIds.length);
    try {
      const results = await resetChunk(chunk, batchId, i === chunks.length - 1);
      batchId = batchId ?? (results ?? []).find((r) => r.batchId)?.batchId ?? null;
      rows.push(...rowsFor(chunk, results));
      sent += chunk.length;
    } catch (e) {
      const error = bulkResetErrorMessage(e);
      const refused = wasRefused(e);
      rows.push(
        ...chunk.map((tripId): BulkResetSummaryRow =>
          refused
            ? { tripId, outcome: "failed", detail: error }
            : { tripId, outcome: "warning", detail: "The request for this trip failed; it may have been reset. Open it to check its events." }
        ),
        ...chunks
          .slice(i + 1)
          .flat()
          .map((tripId): BulkResetSummaryRow => ({
            tripId,
            outcome: "failed",
            detail: "Not sent: the run stopped after an earlier request failed.",
          }))
      );
      return buildSummary(rows, error, batchId);
    }
  }
  return buildSummary(rows, null, batchId);
};

// ---- Queued path (OrderApi BulkEventReset:Queue): one submission, the server does the trips. ----

// OrderApi has no queue configured: fall back to the chunked, synchronous path.
export const isQueueUnavailable = (error: any): boolean =>
  error?.response?.status === 409 && error?.response?.data?.queueUnavailable === true;

// "12 trips selected for processing, 3 skipped" - the immediate confirmation (FR-6).
export const queuedSubmitMessage = (r: QueuedBulkResetResponse): string =>
  `${plural(r.queued, "trip")} selected for processing` + (r.skipped.length ? `, ${r.skipped.length} skipped` : "") + ".";

const tripRow = (t: EventResetBatchTripRow): BulkResetSummaryRow => {
  if (t.status === "Skipped") return { tripId: t.tripId, outcome: "skipped", detail: t.reason || "Not eligible for bulk reset." };
  if (t.status === "Failed") {
    // A trip whose events were deactivated was changed even though it did not finish cleanly.
    return t.deactivatedEventCount != null
      ? { tripId: t.tripId, outcome: "warning", detail: t.reason || "The events were reset, but the trip did not finish. Open it to check." }
      : { tripId: t.tripId, outcome: "failed", detail: t.reason || "The reset failed." };
  }
  if (t.status === "Succeeded") {
    const parts = [
      t.deactivatedEventCount != null ? `Removed ${plural(t.deactivatedEventCount, "event")}` : null,
      t.acceptedSuggestionCount != null ? `accepted ${plural(t.acceptedSuggestionCount, "suggestion")}` : null,
    ].filter(Boolean);
    const approval =
      t.autoApproved === true ? "Auto-approved." : t.autoApproved === false ? `Auto-approve failed: ${t.autoApproveFailReason || "see the trip"}.` : null;
    const detail = [parts.length ? parts.join(", ") + "." : null, approval, t.reason].filter(Boolean).join(" ");
    return { tripId: t.tripId, outcome: "succeeded", detail };
  }
  return { tripId: t.tripId, outcome: "warning", detail: "Still queued or running when this summary was taken." };
};

// The results dialog for a finished queued run, read from its batch.
export const summaryFromBatch = (detail: EventResetBatchDetail): BulkResetSummary =>
  buildSummary(
    detail.trips.map(tripRow),
    detail.batch.status === "Failed" ? detail.batch.notes || "The run failed." : null,
    detail.batch.batchId
  );

export const isBatchFinished = (detail: EventResetBatchDetail): boolean =>
  detail.batch.status !== "Queued" && detail.batch.status !== "Running";

// What a select-all picks from the eligible-trips answer, and the toast that says so: the ids (capped
// at what one reset may take) and why the rest were left out, e.g. "(skipped 6 backhaul, 3 no GPS)".
export interface EligibleSelection {
  tripIds: number[];
  level: "info" | "warning";
  message: string;
}

// OrderApi reports skips by rule name (BulkResetIneligibility); this is how they read to a person.
const SKIP_REASON_LABELS: Record<string, string> = {
  NotFound: "not found",
  NotClosed: "not closed",
  Approved: "already approved",
  LitmusNotFailed: "Litmus not failed",
  NoBeginTrip: "no Begin Trip",
  NoEndTrip: "no End Trip",
  NoGps: "no GPS",
  Cancelled: "cancelled",
  Backhaul: "backhaul",
  SplitOrParentChild: "split or parent/child",
  Rental: "rental",
  TooRecent: "too recent",
  AlreadyQueued: "already queued",
  AlreadyResetUnchanged: "already reset",
};
export const skipReasonLabel = (reason: string): string => SKIP_REASON_LABELS[reason] ?? reason;

export const eligibleTripsSelection = (response: EligibleTripsResponse | null | undefined): EligibleSelection => {
  const tripIds = (response?.tripIds ?? []).slice(0, MAX_BULK_EVENT_RESET);
  if (tripIds.length === 0) {
    return { tripIds, level: "warning", message: "No trips matching these filters can be reset." };
  }
  const skipped = (response?.skipped ?? [])
    .filter((s) => s.count > 0)
    .map((s) => `${s.count} ${skipReasonLabel(s.reason)}`)
    .join(", ");
  const message = `Selected ${plural(tripIds.length, "trip")} that can be reset${skipped ? ` (skipped ${skipped})` : ""}`;
  return { tripIds, level: "info", message };
};
